import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Package, Info, CheckCircle2 } from 'lucide-react';
import IconSelector, { renderCategoryIcon } from './IconSelector';
import { getItemTrackingConfig, validateTrackingConfig, getTrackingTypeDescription } from '../../utils/categoryTracking';
import type { 
  ItemTrackingConfig,
  TrackingType,
  CategoryHierarchy,
  CreateCategoryRequest,
  UpdateCategoryRequest 
} from '../../types/category';

interface CategoryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  category?: CategoryHierarchy | null;
  parentCategories: CategoryHierarchy[];
  onSave: (data: CreateCategoryRequest | UpdateCategoryRequest) => Promise<void>;
}

const CategoryEditModal: React.FC<CategoryEditModalProps> = ({
  isOpen,
  onClose,
  category,
  parentCategories,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    parentCategoryId: '',
    isActive: true,
    // Item tracking configuration
    trackingType: 'SERIALIZED' as TrackingType,
    requiresSerialNumber: true,
    defaultReorderLevel: '' as string | number,
    // Expiration tracking
    requiresExpiration: false,
    defaultExpirationMonths: '' as string | number
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-populate form when editing
  useEffect(() => {
    if (category) {
      // Load existing tracking config using helper (handles backward compatibility)
      const trackingConfig = getItemTrackingConfig(category);
      
      setFormData({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || '',
        parentCategoryId: category.parentCategoryId || '',
        isActive: category.isActive ?? true,
        // Load tracking config
        trackingType: trackingConfig.trackingType,
        requiresSerialNumber: trackingConfig.requiresSerialNumber,
        defaultReorderLevel: trackingConfig.defaultReorderLevel ?? '',
        // Load expiration config
        requiresExpiration: trackingConfig.requiresExpiration ?? false,
        defaultExpirationMonths: trackingConfig.defaultExpirationMonths ?? ''
      });
   } else {
      // Reset form for new category
      setFormData({
        name: '',
        description: '',
        icon: '',
        parentCategoryId: '',
        isActive: true,
        trackingType: 'SERIALIZED',
        requiresSerialNumber: true,
        defaultReorderLevel: '',
        requiresExpiration: false,
        defaultExpirationMonths: ''
      });
    }
    setErrors({});
  }, [category, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'radio') {
      // Handle radio button for tracking type
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleIconChange = (iconKey: string) => {
    setFormData(prev => ({ ...prev, icon: iconKey }));
    if (errors.icon) {
      setErrors(prev => ({ ...prev, icon: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    if (formData.name.length > 50) {
      newErrors.name = 'Category name must be less than 50 characters';
    }

    // Validate reorder level if provided
    if (formData.defaultReorderLevel !== '' && formData.trackingType !== 'SERIALIZED') {
      const reorderVal = Number(formData.defaultReorderLevel);
      if (isNaN(reorderVal) || reorderVal < 0) {
        newErrors.defaultReorderLevel = 'Reorder level must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const saveData: CreateCategoryRequest | UpdateCategoryRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        icon: formData.icon || undefined,
        isActive: formData.isActive
      };

      // Only add parentCategoryId for new categories or when editing parent relationship
      if (!category || formData.parentCategoryId !== (category.parentCategoryId || '')) {
        (saveData as CreateCategoryRequest).parentCategoryId = formData.parentCategoryId || null;
      }

      // Only add item tracking config for subcategories (items with parentCategoryId)
      const isSubcategory = Boolean(formData.parentCategoryId);
      if (isSubcategory) {
        // Build item tracking configuration based on tracking type
        const trackingConfig: ItemTrackingConfig = {
          trackingType: formData.trackingType,
          requiresSerialNumber: formData.trackingType === 'SERIALIZED' ? formData.requiresSerialNumber : false
        };

       // Add defaultReorderLevel for BULK_TRACKED and CONSUMABLE
        if (formData.trackingType !== 'SERIALIZED' && formData.defaultReorderLevel !== '') {
          trackingConfig.defaultReorderLevel = Number(formData.defaultReorderLevel);
        }

        // Add expiration tracking config
        if (formData.requiresExpiration) {
          trackingConfig.requiresExpiration = true;
          if (formData.defaultExpirationMonths !== '') {
            trackingConfig.defaultExpirationMonths = Number(formData.defaultExpirationMonths);
          }
        }

        saveData.itemTrackingConfig = trackingConfig;
      }

      await onSave(saveData);
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
      setErrors({ submit: 'Failed to save category. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      icon: '',
      parentCategoryId: '',
      isActive: true,
      trackingType: 'SERIALIZED',
      requiresSerialNumber: true,
      defaultReorderLevel: '',
      requiresExpiration: false,
      defaultExpirationMonths: ''
    });
    setErrors({});
    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  const isEditing = Boolean(category);
  const isSubcategory = Boolean(formData.parentCategoryId);
  const selectedParent = parentCategories.find(p => p.categoryId === formData.parentCategoryId);

 
  // Helper to get badge color for tracking type
  const getTrackingBadgeStyle = (type: TrackingType) => {
    switch (type) {
      case 'SERIALIZED': return 'bg-blue-100 text-blue-800';
      case 'BULK_TRACKED': return 'bg-green-100 text-green-800';
      case 'CONSUMABLE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrackingLabel = (type: TrackingType) => {
    switch (type) {
      case 'SERIALIZED': return 'Serialized';
      case 'BULK_TRACKED': return 'Bulk Tracked';
      case 'CONSUMABLE': return 'Consumable';
      default: return 'Unknown';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditing ? 'Edit Category' : 'Create New Category'}
          </h2>
          <button 
            onClick={handleClose} 
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-800">{errors.submit}</span>
              </div>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Firearms, Uniforms"
                  disabled={loading}
                  maxLength={50}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Parent Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Category
                </label>
                <select
                  name="parentCategoryId"
                  value={formData.parentCategoryId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  <option value="">None (Top-level)</option>
                  {parentCategories.map((parent) => (
                    <option key={parent.categoryId} value={parent.categoryId}>
                      {parent.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a parent for subcategories
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description..."
                  disabled={loading}
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="rounded"
                  disabled={loading}
                />
                <label className="text-sm text-gray-700">
                  Category is active and available for use
                </label>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Icon Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Icon
                </label>
                <IconSelector
                  value={formData.icon}
                  onChange={handleIconChange}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* Preview Section */}
              {(formData.name || selectedParent) && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                  <div className="flex items-center space-x-2">
                    {formData.icon && (
                      <div className="flex items-center">
                        {renderCategoryIcon(formData.icon, "h-5 w-5 text-gray-600")}
                      </div>
                    )}
                    <div className="text-sm">
                      {selectedParent && (
                        <span className="text-gray-600">{selectedParent.name} → </span>
                      )}
                      <span className="font-medium">{formData.name || 'New Category'}</span>
                      {isSubcategory && (
                        <span className={`ml-2 px-2 py-1 text-xs rounded ${getTrackingBadgeStyle(formData.trackingType)}`}>
                          {getTrackingLabel(formData.trackingType)}
                        </span>
                      )}
                      {!isSubcategory && (
                        <span className="ml-2 px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                          Organizational
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Expiration Tracking - Full Width for Subcategories */}
          {isSubcategory && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="requiresExpiration"
                  checked={formData.requiresExpiration}
                  onChange={handleInputChange}
                  className="mt-1 rounded"
                  disabled={loading}
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-900 cursor-pointer">
                    Requires Expiration Tracking
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Items in this category will require an expiration date (e.g., body armor, OC spray, medical supplies)
                  </p>
                </div>
              </div>
              
              {formData.requiresExpiration && (
                <div className="ml-6 pl-3 border-l-2 border-amber-300">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Warranty/Expiration Period
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      name="defaultExpirationMonths"
                      value={formData.defaultExpirationMonths}
                      onChange={handleInputChange}
                      min="1"
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="60"
                      disabled={loading}
                    />
                    <span className="text-sm text-gray-600">months</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    e.g., 60 months (5 years) for body armor. Used to auto-calculate expiration from manufacture date.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Parent Category Explanation - Full Width */}
          {!isSubcategory && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <Package className="w-4 h-4 text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Parent Category</p>
                  <p>Parent categories are organizational containers. Items are assigned to subcategories, not parent categories directly.</p>
                </div>
              </div>
            </div>
          )}

          {/* Item Tracking Configuration - Full Width for Subcategories */}
          {isSubcategory && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <h4 className="text-sm font-semibold text-gray-900">Item Tracking Configuration</h4>
              </div>
              
              <p className="text-xs text-gray-700">
                Configure how items in this subcategory should be tracked in inventory.
              </p>
              
              {/* Tracking Options - Three Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* SERIALIZED Option */}
                <label className="flex items-start space-x-3 p-3 bg-white border-2 rounded-lg cursor-pointer transition-all hover:border-blue-300 hover:shadow-sm"
                  style={{ borderColor: formData.trackingType === 'SERIALIZED' ? '#3b82f6' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="trackingType"
                    value="SERIALIZED"
                    checked={formData.trackingType === 'SERIALIZED'}
                    onChange={handleInputChange}
                    className="mt-1"
                    disabled={loading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-gray-900">Serialized</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Individual items with serial numbers. Full chain of custody.
                    </p>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Firearms, radios, laptops
                    </p>
                    
                    {formData.trackingType === 'SERIALIZED' && (
                      <div className="mt-3 pl-3 border-l-2 border-blue-400">
                        <label className="flex items-start space-x-2">
                          <input
                            type="checkbox"
                            name="requiresSerialNumber"
                            checked={formData.requiresSerialNumber}
                            onChange={handleInputChange}
                            className="mt-0.5"
                            disabled={loading}
                          />
                          <div>
                            <div className="text-xs font-medium text-gray-800">
                              Require serial number
                            </div>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </label>

                {/* BULK_TRACKED Option */}
                <label className="flex items-start space-x-3 p-3 bg-white border-2 rounded-lg cursor-pointer transition-all hover:border-green-300 hover:shadow-sm"
                  style={{ borderColor: formData.trackingType === 'BULK_TRACKED' ? '#10b981' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="trackingType"
                    value="BULK_TRACKED"
                    checked={formData.trackingType === 'BULK_TRACKED'}
                    onChange={handleInputChange}
                    className="mt-1"
                    disabled={loading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-gray-900">Bulk Tracked</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Track by quantity. Know who has what. Returns restore stock.
                    </p>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Helmets, tactical vests
                    </p>
                  </div>
                </label>

                {/* CONSUMABLE Option */}
                <label className="flex items-start space-x-3 p-3 bg-white border-2 rounded-lg cursor-pointer transition-all hover:border-orange-300 hover:shadow-sm"
                  style={{ borderColor: formData.trackingType === 'CONSUMABLE' ? '#f97316' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="trackingType"
                    value="CONSUMABLE"
                    checked={formData.trackingType === 'CONSUMABLE'}
                    onChange={handleInputChange}
                    className="mt-1"
                    disabled={loading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-gray-900">Consumable</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Stock level only. Returns disposed, not restocked.
                    </p>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Uniforms, gloves, ammo
                    </p>
                  </div>
                </label>
              </div>

              {/* Reorder Level - Only for BULK_TRACKED and CONSUMABLE */}
              {(formData.trackingType === 'BULK_TRACKED' || formData.trackingType === 'CONSUMABLE') && (
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Reorder Level
                  </label>
                  <input
                    type="number"
                    name="defaultReorderLevel"
                    value={formData.defaultReorderLevel}
                    onChange={handleInputChange}
                    min="0"
                    className={`w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.defaultReorderLevel ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 10"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Alert when stock falls to this level. Can be overridden per item.
                  </p>
                  {errors.defaultReorderLevel && (
                    <p className="text-red-500 text-xs mt-1">{errors.defaultReorderLevel}</p>
                  )}
                </div>
              )}

              {/* Current Selection Summary */}
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-xs text-gray-600">
                  <strong className="text-gray-900">Selection:</strong> {getTrackingTypeDescription({
                    trackingType: formData.trackingType,
                    requiresSerialNumber: formData.trackingType === 'SERIALIZED' ? formData.requiresSerialNumber : false
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - Sticky Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : (isEditing ? 'Update Category' : 'Create Category')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryEditModal;
